import { useEffect, useMemo } from "react";

import { Card } from "@jti/core-components/components/Card";
import type { PaymentMethod as PaymentMethodType } from "@jti/fe-common";
import { useSelector } from "@jti/store/reduxHooks";
import type { FieldValues } from "@jti/ui";
import { Form, FormField, useForm, useTranslations } from "@jti/ui";

import { useSubscription } from "@checkout/hooks";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import sortMethodsByEnabled from "@checkout/utils/sortMethodsByEnabled/sortMethodsByEnabled";

import PaymentMethod from "../PaymentMethod";

import styles from "./PaymentMethodSelector.module.scss";

interface FormValues extends FieldValues {
  code: string;
}

type PaymentMethodSelectorProps = {
  methods: PaymentMethodType[];
  onSelect: (code: string) => void;
};

export const PaymentMethodSelector = ({ methods: allMethods, onSelect }: PaymentMethodSelectorProps) => {
  const { __ } = useTranslations();
  const { isSubscription } = useSubscription();
  const { isFreeTrial } = useFreeTrial();
  const { isPgsClientPending, isPaymentProcessing } = useSelector((state) => state.checkout);

  const availableMethods = useMemo(() => {
    return allMethods.filter((m) => m.enabled);
  }, [allMethods]);

  const form = useForm<FormValues>({
    defaultValues: {
      code: "",
    },
  });

  const code = form.watch("code");
  const firstMethodCode = availableMethods.length > 0 && availableMethods[0].code;

  useEffect(() => {
    if (!availableMethods.length) {
      return;
    }

    if (isSubscription || isFreeTrial) {
      const tokenizableMethods = availableMethods.filter((method) => method.tokenizable);
      tokenizableMethods.length && form.setValue("code", tokenizableMethods[0].code);
    } else {
      form.setValue("code", availableMethods[0].code);
    }
  }, [firstMethodCode, isSubscription, isFreeTrial]);

  useEffect(() => {
    onSelect(code);
  }, [onSelect, code]);

  return (
    <div className={styles.wrapper} data-testid="payment-method-selector">
      <Form form={form}>
        <Card data-testid="payment-card" title={__("cart.order.payment_method.title")} variant="level-1">
          <div className={styles.subtext}>{`${__("cart.order.payment_method.choose")}*`}</div>
          <FormField
            name="code"
            validation={{ required: __("validation.field_required") }}
            render={({ field }) => (
              <div className={styles.paymentMethods} role="radiogroup">
                {allMethods.sort(sortMethodsByEnabled).map((method) => {
                  return (
                    <PaymentMethod
                      key={method.code}
                      code={method.code}
                      title={method.title}
                      icon={method.icon}
                      tooltip={method.definition}
                      tokenizable={method.tokenizable || false}
                      isSubscription={isSubscription}
                      isFreeTrial={isFreeTrial}
                      isEnabled={method.enabled && !isPgsClientPending && !isPaymentProcessing}
                      name="code"
                      checked={field.value === method.code}
                      onChange={field.onChange}
                    />
                  );
                })}
              </div>
            )}
          />
        </Card>
      </Form>
    </div>
  );
};
